import isPropValid from '@emotion/is-prop-valid';
import styled from '@emotion/styled';

import { colors, font, headingFont, mq } from '@/lib/theme';

export type TextVariant = 'lead' | 'body' | 'bodySmall' | 'label' | 'caption';
export type TextColor =
	'heading' | 'body' | 'muted' | 'link' | 'inverse' | 'error' | 'success';

const TEXT_COLORS: Record<TextColor, string> = {
	heading: colors.typography.heading,
	body: colors.typography.body,
	muted: colors.LM_NU60,
	link: colors.typography.link,
	inverse: colors.LM_NU0,
	error: colors.RE110,
	success: colors.GR110,
};

const TEXT_VARIANTS: Record<TextVariant, string> = {
	lead: font('IR110'),
	body: font('IR100'),
	bodySmall: font('IR90'),
	label: font('IM90'),
	caption: font('IR80'),
};

interface TextProps {
	variant?: TextVariant;
	color?: TextColor;
	weight?: 'regular' | 'medium' | 'bold';
	align?: 'left' | 'center' | 'right';
}

const styleProps = new Set([
	'variant',
	'color',
	'weight',
	'align',
	'level',
	'size',
]);
const shouldForwardProp = (prop: string) =>
	isPropValid(prop) && !styleProps.has(prop);

const WEIGHTS = { regular: 400, medium: 500, bold: 700 } as const;

/**
 * Body copy. Renders a `<p>` by default; pass `as="span"` for inline text.
 * All sizes come from HUI's Inter scale so copy matches the host portal.
 */
export const Text = styled('p', { shouldForwardProp })<TextProps>`
	${({ variant = 'body' }) => TEXT_VARIANTS[variant]}
	color: ${({ color = 'body' }) => TEXT_COLORS[color]};
	${({ weight }) => weight && `font-weight: ${WEIGHTS[weight]};`}
	${({ align }) => align && `text-align: ${align};`}
	margin: 0;

	strong,
	b {
		font-weight: 700;
		color: ${colors.typography.heading};
	}

	a {
		color: ${colors.typography.link};
		text-decoration: underline;
		text-underline-offset: 2px;
		&:hover {
			color: ${colors.typography.link_hover};
		}
	}
`;

export type HeadingSize = 'display' | 'xl' | 'lg' | 'md' | 'sm' | 'xs';

/** Raleway heading scale. Each size steps down one token on small screens. */
const HEADING_SIZES: Record<HeadingSize, string> = {
	display: `${headingFont('RWB140')} ${mq.md} { ${headingFont('RWB150')} }`,
	xl: `${headingFont('RWB130')} ${mq.md} { ${headingFont('RWB140')} }`,
	lg: `${headingFont('RWB120')} ${mq.md} { ${headingFont('RWB130')} }`,
	md: `${headingFont('RWB110')} ${mq.md} { ${headingFont('RWB120')} }`,
	sm: headingFont('RWB110'),
	xs: headingFont('RWB100'),
};

interface HeadingProps {
	size?: HeadingSize;
	color?: TextColor;
	align?: 'left' | 'center' | 'right';
}

/**
 * Section and page headings. Choose the HTML level for document outline
 * (`as="h2"`) and the visual size separately (`size="lg"`).
 */
export const Heading = styled('h2', { shouldForwardProp })<HeadingProps>`
	${({ size = 'md' }) => HEADING_SIZES[size]}
	color: ${({ color = 'heading' }) => TEXT_COLORS[color]};
	${({ align }) => align && `text-align: ${align};`}
	margin: 0;
	text-wrap: balance;
`;
